import React, { useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
interface CalendarProps {
  value: Date;
  onChange: (date: Date) => void;
  onClose: () => void;
}
const MONTHS = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
const DAYS = ['Lu', 'Ma', 'Mi', 'Ju', 'Vi', 'Sa', 'Do'];
function getDaysInMonth(year: number, month: number): number {
  return new Date(year, month + 1, 0).getDate();
}
function getFirstDayOfMonth(year: number, month: number): number {
  const day = new Date(year, month, 1).getDay();
  return day === 0 ? 6 : day - 1;
}
export function Calendar({
  value,
  onChange,
  onClose
}: CalendarProps) {
  const [viewDate, setViewDate] = useState(value);
  const [mode, setMode] = useState<'day' | 'month' | 'year'>('day');
  const year = viewDate.getFullYear();
  const month = viewDate.getMonth();
  const daysInMonth = getDaysInMonth(year, month);
  const firstDay = getFirstDayOfMonth(year, month);
  const selectedDay = value.getDate();
  const selectedMonth = value.getMonth();
  const selectedYear = value.getFullYear();
  const days: (number | null)[] = [];
  for (let i = 0; i < firstDay; i++) {
    days.push(null);
  }
  for (let i = 1; i <= daysInMonth; i++) {
    days.push(i);
  }
  const handlePrevMonth = () => {
    setViewDate(new Date(year, month - 1, 1));
  };
  const handleNextMonth = () => {
    setViewDate(new Date(year, month + 1, 1));
  };
  const handlePrevYear = () => {
    setViewDate(new Date(year - 1, month, 1));
  };
  const handleNextYear = () => {
    setViewDate(new Date(year + 1, month, 1));
  };
  const handlePrevDecade = () => {
    setViewDate(new Date(year - 10, month, 1));
  };
  const handleNextDecade = () => {
    setViewDate(new Date(year + 10, month, 1));
  };
  const handleDayClick = (day: number) => {
    const newDate = new Date(year, month, day);
    onChange(newDate);
    onClose();
  };
  const handleMonthClick = (monthIndex: number) => {
    setViewDate(new Date(year, monthIndex, 1));
    setMode('day');
  };
  const handleYearClick = (selectedYear: number) => {
    setViewDate(new Date(selectedYear, month, 1));
    setMode('month');
  };
  const handleHeaderClick = () => {
    if (mode === 'day') setMode('month');else if (mode === 'month') setMode('year');
  };
  // Year picker: show 12 years centered around current year
  const startYear = Math.floor(year / 10) * 10;
  const years = Array.from({
    length: 12
  }, (_, i) => startYear + i);
  return <div className="absolute top-full left-0 mt-2 z-50 bg-white border-brutal border-[#1A1A1A] rounded-none shadow-brutal p-4 w-80" data-id="element-28">
      {/* Header */}
      <div className="flex items-center justify-between mb-4" data-id="element-29">
        <button onClick={mode === 'day' ? handlePrevMonth : mode === 'month' ? handlePrevYear : handlePrevDecade} className="p-2 hover:bg-yellow transition-colors" type="button" data-id="element-30">
          <ChevronLeft className="w-5 h-5 text-purple" data-id="element-31" />
        </button>
        <button onClick={handleHeaderClick} className="font-display text-sm font-bold text-purple uppercase hover:text-yellow transition-colors" type="button" data-id="element-32">
          {mode === 'day' && `${MONTHS[month]} ${year}`}
          {mode === 'month' && year}
          {mode === 'year' && `${startYear} - ${startYear + 11}`}
        </button>
        <button onClick={mode === 'day' ? handleNextMonth : mode === 'month' ? handleNextYear : handleNextDecade} className="p-2 hover:bg-yellow transition-colors" type="button" data-id="element-33">
          <ChevronRight className="w-5 h-5 text-purple" data-id="element-34" />
        </button>
      </div>

      {/* Day view */}
      {mode === 'day' && <>
          <div className="grid grid-cols-7 gap-1 mb-2" data-id="element-35">
            {DAYS.map(day => <div key={day} className="text-center font-display text-xs font-bold text-[#1A1A1A] uppercase" data-id="element-36">
                {day}
              </div>)}
          </div>

          <div className="grid grid-cols-7 gap-1" data-id="element-37">
            {days.map((day, index) => {
          if (day === null) {
            return <div key={`empty-${index}`} data-id="element-38" />;
          }
          const isSelected = day === selectedDay && month === selectedMonth && year === selectedYear;
          return <button key={day} onClick={() => handleDayClick(day)} type="button" className={`
                    aspect-square flex items-center justify-center
                    font-display text-sm transition-all duration-100
                    ${isSelected ? 'bg-purple text-white font-bold' : 'hover:bg-yellow text-[#1A1A1A]'}
                  `} data-id="element-39">
                  {day}
                </button>;
        })}
          </div>
        </>}

      {/* Month view */}
      {mode === 'month' && <div className="grid grid-cols-3 gap-2" data-id="element-40">
          {MONTHS.map((monthName, index) => {
        const isSelected = index === selectedMonth && year === selectedYear;
        return <button key={monthName} onClick={() => handleMonthClick(index)} type="button" className={`
                  py-3 px-2 font-display text-sm transition-all duration-100
                  ${isSelected ? 'bg-purple text-white font-bold' : 'hover:bg-yellow text-[#1A1A1A]'}
                `} data-id="element-41">
                {monthName.slice(0, 3)}
              </button>;
      })}
        </div>}

      {/* Year view */}
      {mode === 'year' && <div className="grid grid-cols-3 gap-2" data-id="element-42">
          {years.map(y => {
        const isSelected = y === selectedYear;
        return <button key={y} onClick={() => handleYearClick(y)} type="button" className={`
                  py-3 px-2 font-display text-sm transition-all duration-100
                  ${isSelected ? 'bg-purple text-white font-bold' : 'hover:bg-yellow text-[#1A1A1A]'}
                `} data-id="element-43">
                {y}
              </button>;
      })}
        </div>}
    </div>;
}